import browser from "webextension-polyfill";
import { GET_DIALOG_MESSAGE_TYPE, GET_DIALOG_SHOULD_STILL_SHOW_MESSAGE_TYPE, DIALOG_ACTION_MESSAGE_TYPE, DISMISS_DIALOG_MESSAGE_TYPE, YT_MASTHEAD_TIMEOUT_MS, } from "../shared/constants.js";
import { DialogAction, } from "../shared/types.js";
function sendDialogAction(touchPointStep, action) {
    void browser.runtime.sendMessage({
        type: DIALOG_ACTION_MESSAGE_TYPE,
        touchPointStep,
        action,
    });
}
async function detectYouTubePremium(timeoutMs = YT_MASTHEAD_TIMEOUT_MS) {
    const masthead = document.querySelector("#masthead");
    if (masthead) {
        return masthead.getAttribute("logo-type") === "YOUTUBE_PREMIUM_LOGO";
    }
    return await new Promise((resolve) => {
        const observer = new MutationObserver(() => {
            const el = document.querySelector("#masthead");
            if (el) {
                observer.disconnect();
                resolve(el.getAttribute("logo-type") === "YOUTUBE_PREMIUM_LOGO");
            }
        });
        observer.observe(document.documentElement, {
            childList: true,
            subtree: true,
        });
        setTimeout(() => {
            observer.disconnect();
            resolve(false);
        }, timeoutMs);
    });
}
function isDarkMode() {
    const docAttr = document.documentElement.getAttribute("dark");
    const ytdApp = document.querySelector("ytd-app");
    return docAttr !== null || (ytdApp === null || ytdApp === void 0 ? void 0 : ytdApp.hasAttribute("dark")) === true;
}
let dialogContainer = null;
let dialogRemoved = false;
let autoDismissTimer = null;
let autoDismissMs = null;
let currentTouchPointStep = null;
let startCalled = false;
const DIALOG_STYLES = `
  :host {
    position: fixed;
    top: 37px;
    right: 37px;
    z-index: 2147483647;
    margin: 0;
    padding: 0;
    border: none;
    background: none;
  }

  #dialog {
    position: relative;
    border-radius: 12px;
    padding: 20px 24px;
    width: 300px;
    font-family: Arial, Helvetica, sans-serif;
    font-size: 13px;
  }

  #header {
    display: flex;
    align-items: center;
    margin-bottom: 14px;
    gap: 8px;
  }

  #logo {
    width: 22px;
    height: 22px;
    flex-shrink: 0;
    user-select: none;
  }

  #title {
    font-size: 14px;
    font-weight: 700;
    flex: 1;
    line-height: 1.3;
  }

  #close-btn {
    position: absolute;
    top: 8px;
    right: 8px;
    background: none;
    border: none;
    font-size: 18px;
    cursor: pointer;
    padding: 0 4px;
    line-height: 1;
    user-select: none;
  }

  #cta-btn {
    display: block;
    width: auto;
    margin-top: 18px;
    margin-left: auto;
    margin-right: auto;
    padding: 10px 40px;
    background: #e53935;
    color: #fff;
    border: none;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s;
    font-family: Arial, Helvetica, sans-serif;
  }

  #cta-btn:hover {
    background: #c62828;
  }

  #links-row {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 10px;
    font-size: 13px;
  }

  #links-row.no-cta {
    margin-top: 32px;
  }

  #links-row a {
    text-decoration: none;
  }

  .links-separator {
    user-select: none;
  }

  .heading {
    font-size: 13px;
    font-weight: 600;
    margin: 12px 0 6px 0;
    text-align: left;
  }

  .list-item {
    font-size: 13px;
    line-height: 1.6;
    margin: 0;
    text-align: left;
    padding-left: 8px;
  }

  .check {
    color: #e53935;
    margin-right: 8px;
  }

  .body-text {
    font-size: 13px;
    line-height: 1.5;
    margin: 0 0 8px 0;
    padding: 0 8px;
  }
`;
const DARK_DIALOG_STYLES = `
  #dialog {
    background: #282828;
    color: #e0e0e0;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.5);
  }

  #title {
    color: #fff;
  }

  #close-btn {
    color: #888;
  }

  #close-btn:hover {
    color: #fff;
  }

  #links-row a {
    color: #aaa;
  }

  #links-row a:hover {
    color: #e0e0e0;
  }

  .links-separator {
    color: #aaa;
  }

  .heading {
    color: #e0e0e0;
  }

  .list-item {
    color: #aaa;
  }

  .body-text {
    color: #aaa;
  }

  strong {
    color: #e0e0e0;
  }

  em, u {
    color: inherit;
  }
`;
const LIGHT_DIALOG_STYLES = `
  #dialog {
    background: #ffffff;
    color: #333333;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
  }

  #title {
    color: #111111;
  }

  #close-btn {
    color: #777777;
  }

  #close-btn:hover {
    color: #111111;
  }

  #links-row a {
    color: #666666;
  }

  #links-row a:hover {
    color: #333333;
  }

  .links-separator {
    color: #666666;
  }

  .heading {
    color: #333333;
  }

  .list-item {
    color: #555555;
  }

  .body-text {
    color: #555555;
  }

  strong {
    color: #333333;
  }

  em, u {
    color: inherit;
  }
`;
function appendRichText(parent, text) {
    const parts = text.split(/(<(?:strong|em|u)>.*?<\/(?:strong|em|u)>)/g);
    for (const part of parts) {
        const match = /^<(strong|em|u)>(.*?)<\/\1>$/.exec(part);
        if (match) {
            const el = document.createElement(match[1]);
            el.textContent = match[2];
            parent.appendChild(el);
        }
        else if (part) {
            parent.appendChild(document.createTextNode(part));
        }
    }
}
function renderBodyLine(line) {
    if (line.style === "heading") {
        const el = document.createElement("p");
        el.className = "heading";
        el.textContent = line.text;
        return el;
    }
    if (line.style === "list-item") {
        const el = document.createElement("p");
        el.className = "list-item";
        const check = document.createElement("span");
        check.className = "check";
        check.textContent = "\u2713";
        el.appendChild(check);
        el.appendChild(document.createTextNode(line.text));
        return el;
    }
    const el = document.createElement("p");
    el.className = "body-text";
    appendRichText(el, line.text);
    return el;
}
function removeDialog() {
    dialogRemoved = true;
    currentTouchPointStep = null;
    autoDismissMs = null;
    if (autoDismissTimer !== null) {
        clearTimeout(autoDismissTimer);
        autoDismissTimer = null;
    }
    if (dialogContainer) {
        dialogContainer.remove();
        dialogContainer = null;
    }
}
function startAutoDismissTimer() {
    if (autoDismissMs === null) {
        return;
    }
    const touchPointStep = currentTouchPointStep;
    autoDismissTimer = setTimeout(() => {
        removeDialog();
        if (touchPointStep !== null) {
            sendDialogAction(touchPointStep, DialogAction.AUTO_DISMISS);
        }
    }, autoDismissMs);
}
function stopAutoDismissTimer() {
    if (autoDismissTimer !== null) {
        clearTimeout(autoDismissTimer);
        autoDismissTimer = null;
    }
}
function buildDialog(payload) {
    var _a;
    const host = document.createElement("div");
    const shadow = host.attachShadow({ mode: "closed" });
    const style = document.createElement("style");
    style.textContent =
        DIALOG_STYLES + (isDarkMode() ? DARK_DIALOG_STYLES : LIGHT_DIALOG_STYLES);
    shadow.appendChild(style);
    const dialog = document.createElement("div");
    dialog.id = "dialog";
    const header = document.createElement("div");
    header.id = "header";
    if (payload.logoUrl) {
        const logo = document.createElement("img");
        logo.id = "logo";
        logo.src = payload.logoUrl;
        header.appendChild(logo);
    }
    const title = document.createElement("span");
    title.id = "title";
    title.textContent = payload.title;
    header.appendChild(title);
    dialog.appendChild(header);
    if (payload.showCloseButton) {
        const closeBtn = document.createElement("button");
        closeBtn.id = "close-btn";
        closeBtn.textContent = "\u00d7";
        closeBtn.addEventListener("click", () => {
            sendDialogAction(payload.touchPointStep, DialogAction.CLOSE);
            removeDialog();
        });
        dialog.appendChild(closeBtn);
    }
    for (const line of payload.body) {
        dialog.appendChild(renderBodyLine(line));
    }
    if (payload.ctaButton) {
        const ctaBtn = document.createElement("button");
        ctaBtn.id = "cta-btn";
        ctaBtn.textContent = payload.ctaButton.text;
        const ctaButton = payload.ctaButton;
        ctaBtn.addEventListener("click", () => {
            if (ctaButton.url) {
                window.open(ctaButton.url, "_blank");
            }
            else {
                removeDialog();
            }
            sendDialogAction(payload.touchPointStep, ctaButton.key);
        });
        dialog.appendChild(ctaBtn);
    }
    const links = (_a = payload.links) !== null && _a !== void 0 ? _a : [];
    if (links.length > 0) {
        const linksRow = document.createElement("div");
        linksRow.id = "links-row";
        if (!payload.ctaButton) {
            linksRow.classList.add("no-cta");
        }
        for (let i = 0; i < links.length; i++) {
            const link = links[i];
            const el = document.createElement("a");
            el.textContent = link.text;
            if (link.url) {
                el.href = link.url;
                el.target = "_blank";
            }
            else {
                el.href = "#";
            }
            el.addEventListener("click", (e) => {
                if (!link.url) {
                    e.preventDefault();
                    removeDialog();
                }
                sendDialogAction(payload.touchPointStep, link.key);
            });
            linksRow.appendChild(el);
            if (i < links.length - 1) {
                const sep = document.createElement("span");
                sep.className = "links-separator";
                sep.textContent = "\u2022";
                linksRow.appendChild(sep);
            }
        }
        dialog.appendChild(linksRow);
    }
    shadow.appendChild(dialog);
    return host;
}
function renderDialog(payload) {
    if (dialogContainer !== null || dialogRemoved) {
        return;
    }
    dialogContainer = buildDialog(payload);
    currentTouchPointStep = payload.touchPointStep;
    document.body.appendChild(dialogContainer);
    if (payload.autoDismissMs) {
        autoDismissMs = payload.autoDismissMs;
        startAutoDismissTimer();
        dialogContainer.addEventListener("pointerenter", stopAutoDismissTimer);
        dialogContainer.addEventListener("pointerleave", startAutoDismissTimer);
        dialogContainer.addEventListener("focusin", stopAutoDismissTimer);
        dialogContainer.addEventListener("focusout", startAutoDismissTimer);
    }
}
export async function start() {
    if (startCalled) {
        return;
    }
    startCalled = true;
    document.addEventListener("visibilitychange", () => {
        const needsCheck = document.visibilityState === "visible" &&
            dialogContainer !== null &&
            document.contains(dialogContainer);
        if (needsCheck) {
            void browser.runtime
                .sendMessage({
                type: GET_DIALOG_SHOULD_STILL_SHOW_MESSAGE_TYPE,
                touchPointStep: currentTouchPointStep,
            })
                .then((shouldStillShow) => {
                if (!shouldStillShow) {
                    removeDialog();
                }
            });
        }
    });
    browser.runtime.onMessage.addListener(function dismissListener(message) {
        if ((message === null || message === void 0 ? void 0 : message.type) !== DISMISS_DIALOG_MESSAGE_TYPE) {
            return undefined;
        }
        removeDialog();
        browser.runtime.onMessage.removeListener(dismissListener);
        return Promise.resolve();
    });
    const ytPremium = await detectYouTubePremium();
    let payload = null;
    try {
        payload = await browser.runtime.sendMessage({
            type: GET_DIALOG_MESSAGE_TYPE,
            ytPremium,
        });
    }
    catch (_a) {
        return;
    }
    if (!payload) {
        return;
    }
    renderDialog(payload);
}
